'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { HomeIcon, AcademicCapIcon, ClipboardDocumentListIcon, CalendarDaysIcon, BellIcon, UserCircleIcon, ChartBarIcon, UsersIcon, BookOpenIcon, DocumentTextIcon, CurrencyDollarIcon, BuildingOfficeIcon, Cog6ToothIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardDocumentCheckIcon, PresentationChartLineIcon, UserGroupIcon, DocumentChartBarIcon, BriefcaseIcon, DocumentPlusIcon, BanknotesIcon, ChatBubbleLeftRightIcon, ShieldCheckIcon, ServerStackIcon, BoltIcon, NoSymbolIcon, Cog8ToothIcon, ArrowLeftOnRectangleIcon, HomeModernIcon, TrophyIcon, CheckBadgeIcon, BuildingLibraryIcon, AdjustmentsHorizontalIcon, EnvelopeOpenIcon, ListBulletIcon, FolderOpenIcon, TableCellsIcon, MegaphoneIcon, BeakerIcon, TruckIcon, ComputerDesktopIcon, ShoppingCartIcon, UserPlusIcon, ExclamationTriangleIcon,  } from '@heroicons/react/24/outline';
import Icon from '@/components/ui/AppIcon';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { useNotifications } from '@/hooks/useNotifications';


interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

function getNavItems(role: string): NavGroup[] {
  switch (role) {
    case 'student':
      return [
        {
          group: 'My Portal',
          items: [
            { label: 'Dashboard', href: '/student-portal', icon: HomeIcon },
            { label: 'My Courses', href: '/student-portal', icon: BookOpenIcon },
            { label: 'Timetable', href: '/student-portal', icon: CalendarDaysIcon },
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
          ],
        },
        {
          group: 'Academic',
          items: [
            { label: 'Academic Transcript', href: '/student-portal/transcript', icon: AcademicCapIcon },
            { label: 'Registration Clearance', href: '/student-portal/clearance', icon: ShieldCheckIcon },
            { label: 'Course Resources', href: '/student-portal', icon: FolderOpenIcon },
          ],
        },
        {
          group: 'Finance',
          items: [
            { label: 'Fee Statement', href: '/student-portal/fees', icon: BanknotesIcon },
          ],
        },
        {
          group: 'Requests',
          items: [
            { label: 'Withdrawal / Deferral', href: '/student-portal/withdrawal', icon: ArrowLeftOnRectangleIcon },
          ],
        },
        {
          group: 'Accommodation',
          items: [
            { label: 'My Accommodation', href: '/student-portal/accommodation', icon: HomeModernIcon },
          ],
        },
        {
          group: 'Library',
          items: [
            { label: 'My Library', href: '/student-portal/library', icon: BuildingLibraryIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'My Documents', href: '/student-portal/documents', icon: FolderOpenIcon },
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
            { label: 'Messages', href: '/student-portal/messages', icon: ChatBubbleLeftRightIcon },
          ],
        },
      ];
    case 'staff':
      return [
        {
          group: 'Teaching',
          items: [
            { label: 'Dashboard', href: '/staff-trainer-portal', icon: HomeIcon },
            { label: 'My Classes', href: '/staff-trainer-portal#classes', icon: BookOpenIcon },
            { label: 'Attendance', href: '/staff-trainer-portal#attendance', icon: ClipboardDocumentCheckIcon },
            { label: 'Grade Entry', href: '/staff-trainer-portal#grades', icon: TableCellsIcon },
          ],
        },
        {
          group: 'Reports',
          items: [
            { label: 'Assessment Sheets', href: '/staff-trainer-portal#assessments', icon: DocumentTextIcon },
          ],
        },
        {
          group: 'Communication',
          items: [
            { label: 'Messages', href: '/staff-trainer-portal/messages', icon: ChatBubbleLeftRightIcon },
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    case 'coordinator':
      return [
        {
          group: 'Programs',
          items: [
            { label: 'Dashboard', href: '/coordinator-portal', icon: HomeIcon },
            { label: 'Programs', href: '/coordinator-portal#programs', icon: AcademicCapIcon },
            { label: 'Enrolments', href: '/coordinator-portal#enrolments', icon: UsersIcon, badge: 5 },
            { label: 'Schedule Builder', href: '/coordinator-portal/schedule', icon: CalendarDaysIcon },
            { label: 'Trainer Availability', href: '/coordinator-portal/schedule/availability', icon: NoSymbolIcon },
            { label: 'Trainer Management', href: '/coordinator-portal/trainer-management', icon: UserGroupIcon },
          ],
        },
        {
          group: 'Assessment & Grading',
          items: [
            { label: 'Grade Entry Grid', href: '/coordinator-portal/grading', icon: TableCellsIcon },
            { label: 'GPA & Awards', href: '/coordinator-portal/grading/gpa', icon: TrophyIcon },
            { label: 'Grading Config', href: '/coordinator-portal/grading/config', icon: Cog6ToothIcon },
            { label: 'Master Sheets', href: '/coordinator-portal#master-sheets', icon: ClipboardDocumentListIcon, badge: 2 },
            { label: 'Exam Management', href: '/coordinator-portal/exams', icon: AcademicCapIcon },
            { label: 'Exam Results', href: '/coordinator-portal/exams/results', icon: DocumentTextIcon },
            { label: 'Student Performance', href: '/coordinator-portal/grading/performance', icon: ChartBarIcon },
            { label: 'Attendance Analytics', href: '/coordinator-portal/attendance-analytics', icon: ClipboardDocumentCheckIcon },
          ],
        },
        {
          group: 'Applications',
          items: [
            { label: 'New Application', href: '/apply', icon: DocumentPlusIcon },
            { label: 'Review Applications', href: '/coordinator-portal/applications', icon: ClipboardDocumentCheckIcon },
            { label: 'Offer Letters', href: '/coordinator-portal/offer-letters', icon: DocumentTextIcon },
            { label: 'Withdrawal & Deferral', href: '/coordinator-portal/withdrawals', icon: ArrowLeftOnRectangleIcon },
          ],
        },
        {
          group: 'Accommodation',
          items: [
            { label: 'Accommodation Mgmt', href: '/coordinator-portal/accommodation', icon: HomeModernIcon },
          ],
        },
        {
          group: 'Library',
          items: [
            { label: 'Library Catalogue', href: '/coordinator-portal/library', icon: BuildingLibraryIcon },
            { label: 'Loans & Clearances', href: '/coordinator-portal/library/loans', icon: BookOpenIcon },
          ],
        },
        {
          group: 'Short Courses',
          items: [
            { label: 'Short Courses', href: '/coordinator-portal/short-courses', icon: BookOpenIcon },
          ],
        },
        {
          group: 'Graduation',
          items: [
            { label: 'Assessment & Board', href: '/coordinator-portal/graduation', icon: TrophyIcon },
            { label: 'Registration Clearance', href: '/coordinator-portal/registration-clearance', icon: CheckBadgeIcon },
          ],
        },
        {
          group: 'Reports',
          items: [
            { label: 'Reports', href: '/coordinator-portal/reports', icon: DocumentChartBarIcon },
          ],
        },
        {
          group: 'Communication',
          items: [
            { label: 'Messages', href: '/coordinator-portal/messages', icon: ChatBubbleLeftRightIcon },
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    case 'executive':
      return [
        {
          group: 'Overview',
          items: [
            { label: 'Executive Portal', href: '/executive-portal', icon: HomeIcon },
            { label: 'Executive Dashboard', href: '/executive-dashboard', icon: PresentationChartLineIcon },
            { label: 'Campus Performance', href: '/executive-portal/kpi-scorecard', icon: BuildingOfficeIcon },
            { label: 'Attendance Analytics', href: '/attendance-analytics', icon: ClipboardDocumentCheckIcon },
            { label: 'Reporting & Analytics', href: '/reporting-analytics', icon: DocumentChartBarIcon },
          ],
        },
        {
          group: 'Finance',
          items: [
            { label: 'Finance Overview', href: '/executive-portal/finance-overview', icon: CurrencyDollarIcon },
          ],
        },
        {
          group: 'ERP Modules',
          items: [
            { label: 'HRMS & Payroll', href: '/hrms', icon: UserPlusIcon },
            { label: 'Procurement & Assets', href: '/procurement', icon: ShoppingCartIcon },
            { label: 'Fleet Management', href: '/fleet', icon: TruckIcon },
            { label: 'ICT Service Mgmt', href: '/ict-service', icon: ComputerDesktopIcon },
            { label: 'Marketing & Stakeholders', href: '/marketing', icon: MegaphoneIcon },
            { label: 'Audit & Risk', href: '/audit-risk', icon: ExclamationTriangleIcon },
          ],
        },
        {
          group: 'Endorsements',
          items: [
            { label: 'Board Endorsements', href: '/executive-portal/endorsements', icon: TrophyIcon },
            { label: 'Graduation Tracker', href: '/executive-portal/graduation-tracker', icon: AcademicCapIcon },
          ],
        },
        {
          group: 'Communications',
          items: [
            { label: 'Announcements', href: '/executive-portal/announcements', icon: MegaphoneIcon },
          ],
        },
        {
          group: 'Compliance',
          items: [
            { label: 'Compliance Dashboard', href: '/executive-portal/compliance', icon: ClipboardDocumentCheckIcon },
            { label: 'System Actions', href: '/system-actions', icon: BoltIcon },
            { label: 'Audit Trail', href: '/audit-trail', icon: ShieldCheckIcon },
            { label: 'Activity Log', href: '/activity-log', icon: ListBulletIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    case 'sponsor':
      return [
        {
          group: 'My Sponsorships',
          items: [
            { label: 'Dashboard', href: '/sponsor-portal', icon: HomeIcon },
            { label: 'Sponsored Students', href: '/sponsor-portal', icon: UserGroupIcon },
            { label: 'Fee Payments', href: '/sponsor-portal', icon: CurrencyDollarIcon },
          ],
        },
        {
          group: 'Communication',
          items: [
            { label: 'Messages', href: '/sponsor-portal/messages', icon: ChatBubbleLeftRightIcon },
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    case 'admin':
      return [
        {
          group: 'Administration',
          items: [
            { label: 'Admin Config', href: '/admin-config', icon: Cog8ToothIcon },
            { label: 'Graduation Mgmt', href: '/admin-config/graduation', icon: TrophyIcon },
            { label: 'User Management', href: '/user-management', icon: UsersIcon },
            { label: 'Staff Invitations', href: '/staff-invitations', icon: EnvelopeOpenIcon },
            { label: 'Notification Rules', href: '/notification-rules', icon: BellIcon },
            { label: 'System Onboarding', href: '/admin-onboarding', icon: Cog6ToothIcon },
          ],
        },
        {
          group: 'ERP Modules',
          items: [
            { label: 'HRMS & Payroll', href: '/hrms', icon: UserPlusIcon },
            { label: 'Procurement & Assets', href: '/procurement', icon: ShoppingCartIcon },
            { label: 'Fleet Management', href: '/fleet', icon: TruckIcon },
            { label: 'ICT Service Mgmt', href: '/ict-service', icon: ComputerDesktopIcon },
            { label: 'Marketing & Stakeholders', href: '/marketing', icon: MegaphoneIcon },
            { label: 'Audit & Risk', href: '/audit-risk', icon: ExclamationTriangleIcon },
          ],
        },
        {
          group: 'Monitoring',
          items: [
            { label: 'System Monitor', href: '/system-monitor', icon: ServerStackIcon },
            { label: 'Playwright Monitor', href: '/playwright-monitor', icon: BeakerIcon },
            { label: 'System Actions', href: '/system-actions', icon: BoltIcon },
            { label: 'Audit Trail', href: '/audit-trail', icon: ShieldCheckIcon },
            { label: 'Activity Log', href: '/activity-log', icon: ListBulletIcon },
          ],
        },
        {
          group: 'Analytics',
          items: [
            { label: 'Reporting & Analytics', href: '/reporting-analytics', icon: DocumentChartBarIcon },
            { label: 'Attendance Analytics', href: '/attendance-analytics', icon: ClipboardDocumentCheckIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    case 'reporting':
      return [
        {
          group: 'Analytics',
          items: [
            { label: 'Reports Hub', href: '/reporting-analytics', icon: DocumentChartBarIcon },
            { label: 'Enrollment Reports', href: '/reporting-analytics?category=Enrollment', icon: UsersIcon },
            { label: 'Academic Reports', href: '/reporting-analytics?category=Academic', icon: AcademicCapIcon },
            { label: 'Finance Reports', href: '/reporting-analytics?category=Finance', icon: CurrencyDollarIcon },
            { label: 'Campus Reports', href: '/reporting-analytics?category=Enrollment', icon: BuildingOfficeIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    case 'finance':
      return [
        {
          group: 'Finance',
          items: [
            { label: 'Dashboard', href: '/finance-portal', icon: HomeIcon },
            { label: 'Fee Payments', href: '/finance-portal#payments', icon: CurrencyDollarIcon },
            { label: 'Billing Statements', href: '/finance-portal#billing', icon: DocumentTextIcon },
            { label: 'Reconciliation', href: '/finance-portal#reconciliation', icon: BanknotesIcon },
            { label: 'Withdrawal Refunds', href: '/finance-portal/withdrawals', icon: ArrowLeftOnRectangleIcon },
          ],
        },
        {
          group: 'Communication',
          items: [
            { label: 'Messages', href: '/finance-portal/messages', icon: ChatBubbleLeftRightIcon },
            { label: 'Notifications', href: '/notifications', icon: BellIcon },
          ],
        },
        {
          group: 'My Account',
          items: [
            { label: 'Account Settings', href: '/account-settings', icon: Cog6ToothIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
          ],
        },
      ];
    default:
      return [
        {
          group: 'Navigation',
          items: [
            { label: 'Student Portal', href: '/student-portal', icon: AcademicCapIcon },
            { label: 'Staff Portal', href: '/staff-trainer-portal', icon: BriefcaseIcon },
            { label: 'Coordinator', href: '/coordinator-portal', icon: ClipboardDocumentListIcon },
            { label: 'Executive', href: '/executive-dashboard', icon: ChartBarIcon },
            { label: 'Sponsor', href: '/sponsor-portal', icon: UserGroupIcon },
            { label: 'Finance', href: '/finance-portal', icon: BanknotesIcon },
            { label: 'Reports', href: '/reporting-analytics', icon: DocumentChartBarIcon },
            { label: 'Library Catalogue', href: '/coordinator-portal/library', icon: BuildingLibraryIcon },
            { label: 'Library Loans', href: '/coordinator-portal/library/loans', icon: BookOpenIcon },
            { label: 'Public Library', href: '/library', icon: BuildingLibraryIcon },
            { label: 'Audit Trail', href: '/audit-trail', icon: ShieldCheckIcon },
            { label: 'Activity Log', href: '/activity-log', icon: ListBulletIcon },
            { label: 'System Actions', href: '/system-actions', icon: BoltIcon },
            { label: 'System Monitor', href: '/system-monitor', icon: ServerStackIcon },
            { label: 'Playwright Monitor', href: '/playwright-monitor', icon: BeakerIcon },
            { label: 'System Onboarding', href: '/admin-onboarding', icon: Cog6ToothIcon },
            { label: 'Admin Config', href: '/admin-config', icon: Cog8ToothIcon },
            { label: 'Graduation Mgmt', href: '/admin-config/graduation', icon: TrophyIcon },
            { label: 'Short Courses', href: '/coordinator-portal/short-courses', icon: BookOpenIcon },
            { label: 'Notification Rules', href: '/notification-rules', icon: BellIcon },
            { label: 'User Management', href: '/user-management', icon: UsersIcon },
            { label: 'Staff Invitations', href: '/staff-invitations', icon: EnvelopeOpenIcon },
            { label: 'Account Settings', href: '/account-settings', icon: UserCircleIcon },
            { label: 'Preferences', href: '/account-settings/preferences', icon: AdjustmentsHorizontalIcon },
            { label: 'HRMS & Payroll', href: '/hrms', icon: UserPlusIcon },
            { label: 'Procurement & Assets', href: '/procurement', icon: ShoppingCartIcon },
            { label: 'Fleet Management', href: '/fleet', icon: TruckIcon },
            { label: 'ICT Service Mgmt', href: '/ict-service', icon: ComputerDesktopIcon },
            { label: 'Marketing & Stakeholders', href: '/marketing', icon: MegaphoneIcon },
            { label: 'Audit & Risk', href: '/audit-risk', icon: ExclamationTriangleIcon },
          ],
        },
      ];
  }
}

interface SidebarProps {
  role: string;
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ role, collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const unreadMessages = useUnreadMessages();
  const { unreadCount: unreadNotifications } = useNotifications();

  // Build nav groups and inject live unread count into Messages and Notifications items
  const rawNavGroups = getNavItems(role);
  const navGroups = rawNavGroups.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      if (item.label === 'Messages') {
        return { ...item, badge: unreadMessages > 0 ? unreadMessages : undefined };
      }
      if (item.label === 'Notifications') {
        return { ...item, badge: unreadNotifications > 0 ? unreadNotifications : undefined };
      }
      return item;
    }),
  }));

  return (
    <aside className="sidebar-bg h-full flex flex-col overflow-hidden select-none">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-white/10 ${collapsed ? 'justify-center' : ''}`}>
        <div className="flex-shrink-0 w-9 h-9 bg-white rounded-full flex items-center justify-center overflow-hidden">
          <AppImage
            src="/assets/images/silag-logo-transparent-1785604195033.png"
            alt="SILAG circular institutional seal logo"
            width={32}
            height={32}
            className="object-contain"
          />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <p className="text-white font-bold text-sm leading-tight truncate">SILAG SIMS</p>
            <p className="text-white/60 text-xs truncate">Management System</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1">
        {navGroups.map((group) => (
          <div key={`group-${group.group}`} className="mb-4">
            {!collapsed && (
              <p className="text-sidebar-muted text-[10px] font-700 uppercase tracking-widest px-3 mb-2">
                {group.group}
              </p>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;
              // Exact match always wins; for sub-paths use startsWith only when the
              // href is longer than the portal root (avoids every item that shares
              // the same base href lighting up simultaneously).
              const isActive =
                pathname === item.href ||
                (item.href !== '/' && item.href.split('/').length > 2 &&
                  pathname.startsWith(item.href));
              return (
                <Link
                  key={`nav-${item.href}-${item.label}`}
                  href={item.href}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5
                    transition-all duration-150 group relative
                    ${isActive ? 'sidebar-active text-white' : 'text-white/75 sidebar-hover'}
                  `}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-500 truncate flex-1">{item.label}</span>
                  )}
                  {!collapsed && item.badge && item.badge > 0 ? (
                    <span className="bg-red-500 text-white text-[10px] font-700 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {item.badge}
                    </span>
                  ) : null}
                  {collapsed && item.badge && item.badge > 0 ? (
                    <span className="absolute top-1 right-1 bg-red-500 w-2 h-2 rounded-full" />
                  ) : null}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Toggle + User */}
      <div className="border-t border-white/10 p-2">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-white/60 sidebar-hover transition-all duration-150"
        >
          {collapsed ? (
            <ChevronRightIcon className="w-4 h-4" />
          ) : (
            <>
              <ChevronLeftIcon className="w-4 h-4" />
              <span className="text-xs">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}